import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {IS_SEPARATED} from '@constants/config'

const BUNDLE = /\/app\/build\/root\/main\.([a-f0-9]+)\.js/
const CHECK_INTERVAL = 5 * 60 * 1000

const currentBundleHash = (): string | null => {
	for (const script of Array.from(document.scripts)) {
		const match = script.src.match(BUNDLE)
		if (match) return match[1]
	}
	return null
}

// track serves the same page for every /app route, so it tells which bundle is deployed now.
const deployedBundleHash = async (): Promise<string | null> => {
	try {
		const response = await fetch(window.location.pathname, {cache: 'no-store', credentials: 'same-origin'})
		if (!response.ok) return null
		return (await response.text()).match(BUNDLE)?.[1] ?? null
	} catch {
		return null
	}
}

/**
 * Tabs stay open all day, and in-app navigation never reloads the page, so after a deploy they keep running old code.
 * Once a newer bundle is deployed, the next navigation becomes a full page load instead.
 */
export const useReloadOnNewVersion = () => {
	const location = useLocation()
	const outdated = useRef(false)
	const firstRender = useRef(true)

	useEffect(() => {
		const current = currentBundleHash()
		if (IS_SEPARATED || !current) return

		const check = async () => {
			if (outdated.current) return
			const deployed = await deployedBundleHash()
			if (deployed && deployed !== current) outdated.current = true
		}
		const onVisible = () => document.visibilityState === 'visible' && check()

		const timer = window.setInterval(check, CHECK_INTERVAL)
		document.addEventListener('visibilitychange', onVisible)
		return () => {
			window.clearInterval(timer)
			document.removeEventListener('visibilitychange', onVisible)
		}
	}, [])

	useEffect(() => {
		if (firstRender.current) {
			firstRender.current = false
			return
		}
		if (outdated.current) window.location.reload()
	}, [location.pathname])
}
